import { cupsTarifariosFixture } from './cupsTarifarios.fixture';
import { serviciosTarifariosFixture } from './serviciosTarifarios.fixture';

// Tipos para los datos de fixtures
interface Valor {
  Atributo: string;
  Valor: string;
}

interface CupData {
  Id: number;
  CodigoCup: string;
  DescripcionCup: string;
  Estado: string;
  ServicioId: number;
  MotivoRechazo?: string;
  Valores: Valor[];
}

interface ServicioData {
  Id: number;
  Servicio: string;
  CantidadCups: number;
  CantidadPendientes: number;
  CantidadAprobados: number;
  CantidadRechazados: number;
  Observaciones: string | null;
  Encabezado: string | null;
}

interface Columna {
  Atributo: string;
  Nombre: string;
  Orden: number;
}

interface CupsFixtureResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    __type: string;
    TotalCups: number;
    TotalPendientes: number;
    TotalAprobados: number;
    TotalRechazados: number;
    Columnas: Columna[];
    Cups: CupData[];
  };
}

interface ServiciosFixtureResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    __type: string;
    ProgresoGestion: {
      PorcentajeAprobados: number;
      PorcentajePendientes: number;
      PorcentajeRechazados: number;
      TotalAprobados: number;
      TotalCups: number;
      TotalPendientes: number;
      TotalRechazados: number;
    };
    Servicios: ServicioData[];
  };
}

// Estado mutable de los fixtures para modo desarrollo
class FixturesState {
  private cupsData: CupsFixtureResponse;
  private serviciosData: ServiciosFixtureResponse;

  constructor() {
    // Crear copias profundas de los fixtures originales
    this.cupsData = JSON.parse(JSON.stringify(cupsTarifariosFixture));
    this.serviciosData = JSON.parse(JSON.stringify(serviciosTarifariosFixture));
  }

  // Obtener datos actuales de CUPS
  getCupsData() {
    return this.cupsData;
  }

  // Obtener datos actuales de servicios
  getServiciosData() {
    return this.serviciosData;
  }

  // Actualizar estado de CUPS específicos
  actualizarEstadoCups(cupsIds: number[], nuevoEstado: 'Aprobado' | 'Rechazado') {
    let cambios = 0;
    
    // Actualizar estado de cada CUPS
    this.cupsData.Result.Cups.forEach((cup: CupData) => {
      if (cupsIds.includes(cup.Id)) {
        if (cup.Estado === 'Pendiente') {
          cup.Estado = nuevoEstado;
          cambios++;
        }
      }
    });

    // Recalcular estadísticas
    this.recalcularEstadisticasCups();
    
    console.log(`🔄 Fixtures actualizados: ${cambios} CUPS cambiados a ${nuevoEstado}`);
    console.log('📊 Estadísticas actualizadas:', {
      TotalCups: this.cupsData.Result.TotalCups,
      TotalPendientes: this.cupsData.Result.TotalPendientes,
      TotalAprobados: this.cupsData.Result.TotalAprobados,
      TotalRechazados: this.cupsData.Result.TotalRechazados
    });

    return cambios > 0;
  }

  // Actualizar estado de servicios completos
  actualizarEstadoServicio(servicioIds: number[], nuevoEstado: 'Aprobado' | 'Rechazado') {
    // Para servicios completos, actualizar todos sus CUPS pendientes
    let cambiosServicios = 0;
    
    this.serviciosData.Result.Servicios.forEach((servicio: ServicioData) => {
      if (servicioIds.includes(servicio.Id)) {
        // Simular que se aprueban/rechazan CUPS del servicio
        const cupsDelServicio = this.cupsData.Result.Cups.filter((cup: CupData) => 
          cup.Estado === 'Pendiente' // Solo cambiar los pendientes
        ).slice(0, 2); // Simular que cada servicio tiene algunos CUPS

        cupsDelServicio.forEach((cup: CupData) => {
          cup.Estado = nuevoEstado;
          cambiosServicios++;
        });
      }
    });

    if (cambiosServicios > 0) {
      // Recalcular estadísticas de CUPS y servicios
      this.recalcularEstadisticasCups();
      this.recalcularEstadisticasServicios();
      
      console.log(`🔄 Fixtures actualizados: ${cambiosServicios} CUPS de servicios cambiados a ${nuevoEstado}`);
    }

    return cambiosServicios > 0;
  }

  // Recalcular estadísticas de CUPS
  private recalcularEstadisticasCups() {
    const cups = this.cupsData.Result.Cups;
    const pendientes = cups.filter((cup: CupData) => cup.Estado === 'Pendiente').length;
    const aprobados = cups.filter((cup: CupData) => cup.Estado === 'Aprobado').length;
    const rechazados = cups.filter((cup: CupData) => cup.Estado === 'Rechazado').length;
    const total = cups.length;

    this.cupsData.Result.TotalCups = total;
    this.cupsData.Result.TotalPendientes = pendientes;
    this.cupsData.Result.TotalAprobados = aprobados;
    this.cupsData.Result.TotalRechazados = rechazados;
  }

  // Recalcular estadísticas de servicios
  private recalcularEstadisticasServicios() {
    const cups = this.cupsData.Result.Cups;
    const pendientes = cups.filter((cup: CupData) => cup.Estado === 'Pendiente').length;
    const aprobados = cups.filter((cup: CupData) => cup.Estado === 'Aprobado').length;
    const rechazados = cups.filter((cup: CupData) => cup.Estado === 'Rechazado').length;
    const total = cups.length;

    // Actualizar progreso general
    this.serviciosData.Result.ProgresoGestion.TotalCups = total;
    this.serviciosData.Result.ProgresoGestion.TotalPendientes = pendientes;
    this.serviciosData.Result.ProgresoGestion.TotalAprobados = aprobados;
    this.serviciosData.Result.ProgresoGestion.TotalRechazados = rechazados;
    this.serviciosData.Result.ProgresoGestion.PorcentajePendientes = total > 0 ? (pendientes / total) * 100 : 0;
    this.serviciosData.Result.ProgresoGestion.PorcentajeAprobados = total > 0 ? (aprobados / total) * 100 : 0;
    this.serviciosData.Result.ProgresoGestion.PorcentajeRechazados = total > 0 ? (rechazados / total) * 100 : 0;

    // Actualizar contadores de servicios (simplificado)
    this.serviciosData.Result.Servicios.forEach((servicio: ServicioData) => {
      const cupsDelServicio = Math.floor(total / this.serviciosData.Result.Servicios.length);
      const pendientesDelServicio = Math.floor(pendientes / this.serviciosData.Result.Servicios.length);
      const aprobadosDelServicio = Math.floor(aprobados / this.serviciosData.Result.Servicios.length);
      
      servicio.CantidadCups = cupsDelServicio;
      servicio.CantidadPendientes = pendientesDelServicio;
      servicio.CantidadAprobados = aprobadosDelServicio;
      servicio.CantidadRechazados = cupsDelServicio - pendientesDelServicio - aprobadosDelServicio;
    });
  }

  // Verificar si quedan CUPS pendientes
  tieneCupsPendientes(): boolean {
    return this.cupsData.Result.Cups.some(cup => cup.Estado === 'Pendiente');
  }

  // Resetear fixtures a estado original
  reset() {
    this.cupsData = JSON.parse(JSON.stringify(cupsTarifariosFixture));
    this.serviciosData = JSON.parse(JSON.stringify(serviciosTarifariosFixture));
    console.log('🔄 Fixtures reseteados al estado original');
  }
}

// Instancia global para modo desarrollo
export const fixturesState = new FixturesState();